import React from 'react';

/**
 * Top-level React ErrorBoundary (iter331c).
 *
 * Prevents a render-time crash on any single page from blanking the
 * entire CRM. Rendered just inside `App.js` around `<Routes>` so every
 * authenticated and public route is covered. On error it swallows the
 * exception, logs the full stack to the console (so QA/engineering can
 * copy it), and shows a friendly recovery UI with:
 *   - the error title
 *   - the first-line message
 *   - "Reload page" + "Go to Insights" actions
 *   - optional collapsible stack trace (dev-only)
 *
 * The fallback is intentionally self-contained — no shared components,
 * no router hooks — so an error inside those modules cannot cascade.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // Expose full stack so QA / testing_agent can capture it from the
    // browser console. We never swallow — always re-log.
    // eslint-disable-next-line no-console
    console.error('[CRM ErrorBoundary] uncaught render error:', error, info);
    this.setState({ info });
  }

  reset = () => this.setState({ error: null, info: null });

  render() {
    const { error, info } = this.state;
    if (!error) return this.props.children;

    const isDev = (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development');
    const message = (error && (error.message || String(error))) || 'Unknown error';
    const componentStack = info && info.componentStack ? info.componentStack : '';

    return (
      <div
        className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6"
        data-testid="crm-error-boundary"
        role="alert"
      >
        <div className="max-w-xl w-full bg-white rounded-xl border border-red-200 shadow-sm p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center shrink-0 text-xl font-bold">!</div>
            <div className="flex-1">
              <h1 className="text-lg font-semibold text-gray-900">Something went wrong on this page.</h1>
              <p className="text-sm text-gray-600 mt-1">
                The rest of the CRM is still working. You can reload this page or jump
                back to a known-good screen below. The error has been logged to the
                browser console.
              </p>
              <div
                className="mt-4 text-xs font-mono bg-red-50 border border-red-200 rounded p-2 text-red-800 break-words"
                data-testid="crm-error-boundary-message"
              >
                {message}
              </div>
              {(isDev && componentStack) && (
                <details className="mt-3 text-xs text-gray-600" data-testid="crm-error-boundary-stack">
                  <summary className="cursor-pointer select-none">Component stack</summary>
                  <pre className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded max-h-64 overflow-auto whitespace-pre-wrap">
                    {componentStack}
                  </pre>
                </details>
              )}
              <div className="mt-5 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => { this.reset(); window.location.reload(); }}
                  className="px-3 py-1.5 rounded-md bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium"
                  data-testid="crm-error-boundary-reload"
                >
                  Reload page
                </button>
                <button
                  type="button"
                  onClick={() => { this.reset(); window.location.assign('/insights'); }}
                  className="px-3 py-1.5 rounded-md bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium"
                  data-testid="crm-error-boundary-insights"
                >
                  Go to Insights
                </button>
                <button
                  type="button"
                  onClick={this.reset}
                  className="px-3 py-1.5 rounded-md bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 text-sm font-medium"
                  data-testid="crm-error-boundary-retry"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
